import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import store from "@/store/store.js";
import App from "./App";
import "./scss/app.scss";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://c72b73c4ae161b29bf1a470e1e027460@o4508876242223104.ingest.de.sentry.io/4508913848352848",
  environment: process.env.REACT_APP_SENTRY_ENV || "local",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "https://api.bigbro.ai",
    "https://stageapi.bigbro.ai",
    "https://devapi.bigbro.ai",
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Add beforeSend hook to enrich all errors with user context and backend error details
  beforeSend(event, hint) {
    // Get current user from Redux store
    const state = store.getState();
    const user = state.auth?.user;

    // Set user context if available
    if (user?.username) {
      event.user = {
        username: user.username,
        email: user.email,
        id: user.pk,
        is_staff: user.is_staff,
        is_active: user.is_active,
      };
    }

    // Extract backend error information from the original error
    const originalError = hint.originalException;
    if (originalError?.response?.data) {
      const backendError = originalError.response.data;

      // Add backend error context
      event.contexts = {
        ...event.contexts,
        backend_error: {
          status: originalError.response.status,
          statusText: originalError.response.statusText,
          url: originalError.response.config?.url,
          method: originalError.response.config?.method?.toUpperCase(),
          message: backendError.message || backendError.detail,
          non_field_errors: backendError.non_field_errors,
          field_errors: Object.keys(backendError)
            .filter(
              (key) => !["message", "detail", "non_field_errors"].includes(key)
            )
            .reduce((acc, key) => {
              acc[key] = backendError[key];
              return acc;
            }, {}),
        },
      };

      // Add tags for easier filtering
      event.tags = {
        ...event.tags,
        backend_error: true,
        http_status: originalError.response.status,
        api_endpoint:
          originalError.response.config?.url?.split("/").pop() || "unknown",
      };

      // Add backend error message to event message if available
      const backendMessage =
        backendError.message ||
        backendError.detail ||
        (backendError.non_field_errors &&
          backendError.non_field_errors.join(", "));

      if (backendMessage) {
        event.message = `Backend Error: ${backendMessage}`;
      }
    }

    return event;
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <DndProvider backend={HTML5Backend}>
        <App />
      </DndProvider>
    </Provider>
  </BrowserRouter>
);
